import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { venturesApi, api } from "@/lib/api-client";
import { fmtUSDFull, CATEGORY_META } from "@/lib/format";
import { TrendingUp, TrendingDown, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function NetWorthPulse({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expired, setExpired] = useState(false);

  const load = async () => {
    try {
      const p = await venturesApi.pulse();
      setData(p);
      setExpired(false);
    } catch (e) {
      // FULFILLMENT TRANSPARENCY — a 401 is a session problem, not missing data
      if (e?.response?.status === 401) setExpired(true);
      else toast.error("Failed to load Pulse");
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  // Sovereign Bridge — estate pulses stream in from sibling apps; poll every 60s
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Revenue Projection — First-Dollar / First-$500 from real ledger history
  const [proj, setProj] = useState(null);
  useEffect(() => {
    api.get("/arbitrage/projection").then((r) => setProj(r.data)).catch(() => {});
  }, [refreshKey]);

  const refreshMarket = async () => {
    setRefreshing(true);
    try {
      const r = await venturesApi.refreshMarket();
      toast.success(`Live Market Pulse — ${r.updated} position${r.updated !== 1 ? "s" : ""} synced (${r.crypto_positions} crypto · ${r.stock_positions} stock)`);
      await load();
    } catch (e) { toast.error("Market sync failed"); }
    finally { setRefreshing(false); }
  };

  const total = data?.total || 0;
  const history = (data?.history || []).map((h) => ({
    t: new Date(h.timestamp).getTime(),
    v: h.total_value,
    label: new Date(h.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));
  const first = history[0]?.v || 0;
  const delta = total - first;
  const deltaPct = first ? (delta / first) * 100 : 0;
  const positive = delta >= 0;

  return (
    <section className="uc-card p-6 md:p-8 uc-fade" data-testid="net-worth-pulse">
      {expired && (
        <div className="border border-rose-500/40 bg-rose-500/[0.06] px-3 py-2.5 flex items-center gap-2 flex-wrap mb-4"
             data-testid="pulse-session-expired">
          <span className="uc-dot bg-rose-400"></span>
          <span className="font-mono-uc text-[10px] uppercase tracking-widest text-rose-300">
            Session Expired — Re-Login Required
          </span>
          <span className="text-[10px] text-white/50">Pulse data is withheld, not missing.</span>
          <button data-testid="pulse-relogin-btn" onClick={() => (window.location.href = "/login")}
                  className="ml-auto uc-chip text-rose-300 hover:bg-rose-500/10 hover:border-rose-500/50">
            Re-Login
          </button>
        </div>
      )}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="uc-chip mb-3">
            <span className="uc-dot uc-pulsing bg-amber-400"></span>
            NET WORTH PULSE · LIVE
          </div>
          <div className="flex items-baseline gap-4 flex-wrap">
            <h1 className="font-serif-display text-5xl md:text-6xl leading-none" data-testid="pulse-total">
              {fmtUSDFull(total)}
            </h1>
            <div className={`font-mono-uc text-sm flex items-center gap-1 ${positive ? "text-emerald-400" : "text-red-400"}`}>
              {positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {positive ? "+" : ""}{fmtUSDFull(delta)} ({deltaPct.toFixed(2)}%)
              <span className="text-white/40 ml-1">· trailing</span>
            </div>
          </div>
        </div>
        <button
          data-testid="pulse-refresh-market-btn"
          onClick={refreshMarket}
          disabled={refreshing}
          className="uc-chip hover:bg-white/5 transition-colors uc-focus"
        >
          <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
          Sync Markets · Auto 15m
        </button>
      </div>

      <div className="h-56 md:h-64 -mx-4" style={{ minHeight: 224 }}>
        {history.length > 0 && (
          <ResponsiveContainer width="100%" height="100%" minHeight={200} initialDimension={{ width: 520, height: 224 }}>
            <AreaChart data={history} margin={{ left: 16, right: 16, top: 8, bottom: 4 }}>
              <defs>
                <linearGradient id="pulseGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.35}/>
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="label" hide />
              <YAxis hide domain={["auto", "auto"]} />
              <Tooltip
                cursor={{ stroke: "rgba(245,158,11,0.4)", strokeWidth: 1 }}
                contentStyle={{
                  background: "#121212", border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12,
                }}
                labelStyle={{ color: "#A1A1AA", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}
                formatter={(v) => [fmtUSDFull(v), "Total"]}
              />
              <Area type="monotone" dataKey="v" stroke="#F59E0B" strokeWidth={2} fill="url(#pulseGrad)" isAnimationActive />
              <ReferenceLine y={first} stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
            </AreaChart>
          </ResponsiveContainer>
        )}
        {loading && <div className="h-full flex items-center justify-center text-white/40 text-sm">Loading pulse…</div>}
      </div>

      {/* Controller tax + liquidity strip */}
      {(data?.estimated_tax_liability > 0 || data?.cash_on_hand > 0 || data?.min_liquidity > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-6 pt-6 border-t border-white/10" data-testid="pulse-controller-strip">
          <div className="p-3 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <span className="uc-dot bg-emerald-400"></span>
              <span className="font-mono-uc text-[10px] uppercase tracking-wider text-white/60">Net of Tax</span>
            </div>
            <div className="font-serif-display text-2xl" data-testid="pulse-net-of-tax">{fmtUSDFull(data?.net_of_tax || total)}</div>
            <div className="font-mono-uc text-[11px] text-white/40 mt-1">after est. liability</div>
          </div>
          <div className="p-3 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <span className="uc-dot bg-rose-400"></span>
              <span className="font-mono-uc text-[10px] uppercase tracking-wider text-white/60">Tax Liability</span>
            </div>
            <div className="font-serif-display text-2xl text-rose-300" data-testid="pulse-tax-liability">{fmtUSDFull(data?.estimated_tax_liability || 0)}</div>
            <div className="font-mono-uc text-[11px] text-white/40 mt-1">controller estimate</div>
          </div>
          <div className="p-3 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <span className="uc-dot bg-teal-400"></span>
              <span className="font-mono-uc text-[10px] uppercase tracking-wider text-white/60">Cash On Hand</span>
            </div>
            <div className="font-serif-display text-2xl" data-testid="pulse-cash">{fmtUSDFull(data?.cash_on_hand || 0)}</div>
            <div className="font-mono-uc text-[11px] text-white/40 mt-1">
              net {fmtUSDFull(data?.monthly_net_profit || 0)}/mo
            </div>
          </div>
          <div className="p-3 border border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <span className={`uc-dot ${((data?.cash_on_hand || 0) < (data?.min_liquidity || 0)) ? "bg-red-500 uc-pulsing" : "bg-amber-400"}`}></span>
              <span className="font-mono-uc text-[10px] uppercase tracking-wider text-white/60">Liquidity Floor</span>
            </div>
            <div className="font-serif-display text-2xl" data-testid="pulse-min-liq">{fmtUSDFull(data?.min_liquidity || 0)}</div>
            <div className={`font-mono-uc text-[11px] mt-1 ${((data?.cash_on_hand || 0) < (data?.min_liquidity || 0)) ? "text-red-400" : "text-white/40"}`}>
              {((data?.cash_on_hand || 0) < (data?.min_liquidity || 0)) ? "GUARD BREACHED" : "guard nominal"}
            </div>
          </div>
        </div>
      )}

      {/* Revenue Projection — real ledger history only, labeled projection */}
      {proj && (
        <div className="mt-4 flex items-center gap-3 flex-wrap border border-white/5 px-3 py-2"
             data-testid="revenue-projection-strip">
          <span className="uc-dot bg-emerald-400"></span>
          <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">Umbrella Arbitrage Projection</span>
          <span className="font-mono-uc text-[10px] text-white/70">
            first dollar {proj.first_dollar_date || "—"} · ${(proj.confirmed_to_date || 0).toLocaleString()} confirmed
          </span>
          <span className="font-mono-uc text-[10px] text-amber-300 ml-auto" data-testid="first-500-line">
            First $500: {proj.first_500_status === "ACHIEVED" ? "ACHIEVED" :
              proj.first_500_date ? `${proj.first_500_date} · ${proj.first_500_status} · ${proj.confidence_pct}% confidence` :
              proj.first_500_status}
          </span>
        </div>
      )}

      {/* Sovereign Bridge · Estate Pulse — live revenue/milestone stream from sibling apps */}
      {data?.estate_pulses !== null && data?.estate_pulses !== undefined && (
        <div className="mt-6 pt-6 border-t border-white/10" data-testid="estate-pulse-strip">
          <div className="flex items-center gap-2 mb-3">
            <span className="uc-dot uc-pulsing bg-sky-400"></span>
            <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">
              Sovereign Bridge · Estate Pulse
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { app: "checkmate_payroll", label: "Checkmate Payroll", accent: "text-sky-300" },
              { app: "gifted_os", label: "GIFTED OS", accent: "text-fuchsia-300" },
              ...(data?.estate_registry || []).map((r) => ({
                app: r.app_id, label: r.name, accent: "text-amber-300", genesis: true, lead: r.lead_agent,
              })),
            ].map(({ app, label, accent, genesis, lead }) => {
              const p = (data.estate_pulses || []).find((r) => r.app === app);
              const live = p && (Date.now() - new Date(p.received_at).getTime()) < 24 * 3600 * 1000;
              return (
                <div key={app} className="p-3 border border-white/5 hover:border-white/15 transition-colors"
                     data-testid={`estate-pulse-${app}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`uc-dot ${live ? "bg-emerald-400 uc-pulsing" : "bg-white/25"}`}></span>
                    <span className={`font-mono-uc text-[10px] uppercase tracking-wider ${accent}`}>{label}</span>
                    {genesis && (
                      <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1 py-0.5 border border-amber-500/40 text-amber-400"
                            data-testid={`estate-pulse-${app}-genesis-tag`}>
                        GENESIS · {lead}
                      </span>
                    )}
                    <span className="font-mono-uc text-[9px] uppercase tracking-widest ml-auto text-white/40"
                          data-testid={`estate-pulse-${app}-status`}>
                      {live ? "LIVE" : p ? "STALE" : "STANDBY"}
                    </span>
                  </div>
                  {p ? (
                    <>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                        {Object.entries(p.metrics || {}).slice(0, 4).map(([k, v]) => (
                          <div key={k} className="flex items-baseline justify-between gap-2">
                            <span className="font-mono-uc text-[10px] text-white/40 truncate">{k.replace(/_/g, " ")}</span>
                            <span className="font-serif-display text-lg">
                              {typeof v === "number"
                                ? (/usd|revenue|mrr/.test(k) ? fmtUSDFull(v) : v.toLocaleString())
                                : String(v)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="font-mono-uc text-[10px] text-white/40 mt-2">
                        received {new Date(p.received_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </>
                  ) : (
                    <div className="font-mono-uc text-[11px] text-white/40">
                      {genesis
                        ? "Genesis launched — pre-registered, awaiting first signed pulse."
                        : "Awaiting first signed pulse — bridge armed, handshake prompt issued."}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Category breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-6 pt-6 border-t border-white/10">
        {Object.entries(CATEGORY_META).map(([k, meta]) => {
          const val = data?.by_category?.[k] || 0;
          const pct = total ? (val / total) * 100 : 0;
          return (
            <div key={k} className="p-3 border border-white/5 hover:border-white/15 transition-colors" data-testid={`pulse-cat-${k}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="uc-dot" style={{ background: meta.color }}></span>
                <span className="font-mono-uc text-[10px] uppercase tracking-wider text-white/60">{meta.label}</span>
              </div>
              <div className="font-serif-display text-2xl">{fmtUSDFull(val)}</div>
              <div className="font-mono-uc text-[11px] text-white/40 mt-1">{pct.toFixed(1)}% of total</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
